import { Popover } from '@uhg-abyss/web/ui/Popover';
import { Text } from '@uhg-abyss/web/ui/Text';
import _ from 'lodash';

import translationsEnJson from '../../../public/locale/transperfect/en/translation.json';
import {
  getAoeCodesForEAP,
  getEapBHProgramId,
} from '../../frontends/ProviderSearch/routes/SearchResults/utils';
import { Option, ProviderSearchFilters } from '../../models/ProviderSearch';
import {
  coachmarkPopoverStyles,
  compareSharePopoverStyles,
} from '../CompareShareToggle/CompareShareToggle.styled';
import {
  Constants,
  EAP_OPTION_LABEL,
  EAP_OPTION_LABEL_ES,
  FilterCollectionModel,
  ReverseCoverageTypesCodes,
  SectionType,
  VirtualCareOptions,
} from '../Constants';
import { isPrioritySchedulingActive } from '../ContentFilters/BccFilterUtils';
import { getBestMatchOptionKey } from '../ContentFilters/SortByUtil';

export const getDistanceFilterOption = (
  value: number,
  previousValue?: number
) => [
  {
    label: `${value} ${translationsEnJson.miles}`,
    value,
    dnt_label: `${value} ${translationsEnJson.miles}`,
    c_value: value,
    hide: !value,
    previousValue,
  },
];

export const getFilterValue = (
  filterData: ProviderSearchFilters,
  model: FilterCollectionModel
) =>
  Array.isArray(filterData?.[model])
    ? filterData?.[model].length > 0
      ? filterData?.[model]?.[0]?.value
      : false
    : filterData?.[model]?.value;

export const getAllFilterValue = (
  filterData: ProviderSearchFilters,
  model: FilterCollectionModel
) =>
  Array.isArray(filterData?.[model])
    ? filterData?.[model]
      ? filterData?.[model].map((option) => option.value).toString()
      : false
    : filterData?.[model]?.value;

export const getLatestFilterValue = (
  filterData: ProviderSearchFilters,
  model: FilterCollectionModel
) =>
  Array.isArray(filterData?.[model])
    ? filterData?.[model].length > 0
      ? filterData?.[model]
      : []
    : filterData?.[model]?.value;

export const getIsVirtualCareV2 = (
  enableBHVirtualCareFilterV2: boolean,
  coverageType: string | undefined,
  sectionType: string | undefined
) =>
  enableBHVirtualCareFilterV2 &&
  coverageType === ReverseCoverageTypesCodes['BEHAVIORAL HEALTH'] &&
  sectionType === SectionType.PROVIDER;

export const getVirtualCareModel = (
  enableBHVirtualCareFilterV2: boolean,
  coverageType: string | undefined,
  sectionType: string | undefined
) => {
  const isVirtualCareV2 = getIsVirtualCareV2(
    enableBHVirtualCareFilterV2,
    coverageType,
    sectionType
  );
  return isVirtualCareV2
    ? FilterCollectionModel.VIRTUAL_CARE_OPTIONS
    : FilterCollectionModel.VIRTUAL_CARE;
};

const setEAPOptionForEAPClientOnly = (
  eapOnlyClientOption: Option[],
  filterData: ProviderSearchFilters
) => {
  if (
    !(
      filterData[FilterCollectionModel.EAP_OFFERED] &&
      filterData[FilterCollectionModel.EAP_OFFERED].length > 0
    )
  ) {
    filterData = {
      ...filterData,
      [FilterCollectionModel.EAP_OFFERED]: eapOnlyClientOption,
    };
  }
  return filterData;
};

// Helper function to handle EAP offered to AOE transfer when EAP is added
const handleEapOfferedAdded = (
  filterData: ProviderSearchFilters,
  aoeOptions: any
) => {
  if (
    filterData[FilterCollectionModel.AREA_OF_EXPERTISE] &&
    filterData[FilterCollectionModel.AREA_OF_EXPERTISE]?.length > 0 &&
    filterData[FilterCollectionModel.EAP_OFFERED] !== undefined
  ) {
    return {
      ...filterData,
      [FilterCollectionModel.AREA_OF_EXPERTISE]: aoeOptions?.concat(
        filterData[FilterCollectionModel.EAP_OFFERED]
      ),
    };
  }
  return {
    ...filterData,
    [FilterCollectionModel.AREA_OF_EXPERTISE]:
      filterData[FilterCollectionModel.EAP_OFFERED],
  };
};

// Helper function to handle EAP offered removal
const handleEapOfferedRemoved = (filterData: ProviderSearchFilters) => ({
  ...filterData,
  [FilterCollectionModel.AREA_OF_EXPERTISE]: filterData[
    FilterCollectionModel.AREA_OF_EXPERTISE
  ]?.filter((option) => option?.dnt_label !== EAP_OPTION_LABEL),
});

// Helper function to handle AOE updates
const handleAoeUpdated = (
  filterData: ProviderSearchFilters,
  eapOptionsfromAoe: any,
  aoeOptions: any
) => {
  const updatedData = { ...filterData };

  if (eapOptionsfromAoe && eapOptionsfromAoe.length > 0) {
    updatedData[FilterCollectionModel.EAP_OFFERED] = eapOptionsfromAoe;
  } else if (
    Array.isArray(updatedData[FilterCollectionModel.EAP_OFFERED]) &&
    updatedData[FilterCollectionModel.EAP_OFFERED].some(
      (option) =>
        option?.dnt_label === EAP_OPTION_LABEL ||
        option?.dnt_label === EAP_OPTION_LABEL_ES
    )
  ) {
    // Preserve explicit EAP selection when AOE options are refreshed and no longer include EAP.
  } else {
    updatedData[FilterCollectionModel.EAP_OFFERED] = [];
  }

  if (aoeOptions && aoeOptions.length > 0) {
    updatedData[FilterCollectionModel.AREA_OF_EXPERTISE] = aoeOptions;
  }

  return updatedData;
};

// Helper to clean up array filters to single objects
const cleanupArrayFilters = (filterData: ProviderSearchFilters) => {
  const updatedFilters = { ...filterData };

  if (
    Array.isArray(updatedFilters?.PlatinumProviderIndicator) &&
    updatedFilters.PlatinumProviderIndicator.length > 0
  ) {
    updatedFilters.PlatinumProviderIndicator =
      updatedFilters.PlatinumProviderIndicator[0];
  }

  if (
    Array.isArray(updatedFilters?.ProviderScheduling) &&
    updatedFilters.ProviderScheduling.length > 0
  ) {
    updatedFilters.ProviderScheduling = updatedFilters.ProviderScheduling[0];
  }

  return updatedFilters;
};

// Helper to get valid distance value
const getValidDistance = (
  distanceFilterValue: number,
  autoSearchRadius: number,
  defaultSearchRadius: number
) => {
  if (distanceFilterValue && distanceFilterValue > 0) {
    return distanceFilterValue;
  }
  if (autoSearchRadius && autoSearchRadius > 0) {
    return autoSearchRadius;
  }
  return defaultSearchRadius;
};

// Helper to handle virtual care distance updates
const updateVirtualCareDistance = (
  filterData: ProviderSearchFilters,
  selectedFilters: ProviderSearchFilters,
  currentVirtualCareFilterValue: boolean,
  previousVirtualCareFilterValue: boolean,
  autoSearchRadius: number,
  defaultSearchRadius: number
) => {
  const distanceFilterValue = getFilterValue(
    selectedFilters,
    FilterCollectionModel.DISTANCE
  ) as number;

  const currentDistanceValue = getFilterValue(
    filterData,
    FilterCollectionModel.DISTANCE
  ) as number;

  // Ensure defaultSearchRadius is a number
  const defaultRadius = Number(defaultSearchRadius) || 5;

  // Treats both V1 (boolean true) and V2 (OFFERS_VIRTUAL_VISITS string) as "virtual care active"
  const isVirtualCareActive = (val: any) =>
    val === true || val === VirtualCareOptions.OFFERS_VIRTUAL_VISITS;
  if (
    isVirtualCareActive(currentVirtualCareFilterValue) &&
    !isVirtualCareActive(previousVirtualCareFilterValue)
  ) {
    const currentDistance = getValidDistance(
      distanceFilterValue,
      autoSearchRadius,
      defaultRadius
    );
    return {
      ...filterData,
      [FilterCollectionModel.DISTANCE]: getDistanceFilterOption(
        0,
        currentDistance
      ),
    };
  }

  // Handle when virtual care is turned OFF or becomes undefined (cleared)
  // Check if distance is 0 or -1 (both indicate virtual care was ON)
  if (
    !isVirtualCareActive(currentVirtualCareFilterValue) &&
    isVirtualCareActive(previousVirtualCareFilterValue) &&
    (distanceFilterValue === 0 ||
      distanceFilterValue === -1 ||
      distanceFilterValue < 0)
  ) {
    // When clearing ALL filters, filterData will have the default distance from defaultSearchFilters
    // Check if filterData already has a valid distance value (from defaultSearchFilters)
    const filterDataHasValidDistance =
      currentDistanceValue && currentDistanceValue > 0;

    // If filterData has a valid distance (e.g., from defaultSearchFilters when clearing all filters),
    // use it. Otherwise, fall back to previousValue logic for virtual care toggle.
    if (filterDataHasValidDistance) {
      return {
        ...filterData,
        [FilterCollectionModel.DISTANCE]: getDistanceFilterOption(
          Number(currentDistanceValue)
        ),
      };
    }

    // For virtual care toggle (not clearing all filters), restore from previousValue
    const previousValue = selectedFilters?.[FilterCollectionModel.DISTANCE]?.[0]
      ?.previousValue as number;
    const validAutoSearchRadius =
      autoSearchRadius && autoSearchRadius > 0 ? autoSearchRadius : undefined;
    const restoredDistance =
      previousValue && previousValue > 0
        ? previousValue
        : validAutoSearchRadius || defaultRadius;

    return {
      ...filterData,
      [FilterCollectionModel.DISTANCE]: getDistanceFilterOption(
        Number(restoredDistance)
      ),
    };
  }

  // CASE 3: Handle when distance is -1 or negative (virtual care was ON) but both current and previous virtual care are false
  // This happens when clearing filters via chips after virtual care has already been turned off
  if (
    (distanceFilterValue === -1 ||
      distanceFilterValue < 0 ||
      currentDistanceValue === -1 ||
      currentDistanceValue < 0) &&
    !currentVirtualCareFilterValue &&
    !previousVirtualCareFilterValue
  ) {
    // When clearing filters via chips, filterData will have the default distance from defaultSearchFilters
    const filterDataHasValidDistance =
      currentDistanceValue && currentDistanceValue > 0;

    if (filterDataHasValidDistance) {
      return {
        ...filterData,
        [FilterCollectionModel.DISTANCE]: getDistanceFilterOption(
          Number(currentDistanceValue)
        ),
      };
    }

    // Otherwise restore to default
    const previousValue = selectedFilters?.[FilterCollectionModel.DISTANCE]?.[0]
      ?.previousValue as number;
    const validAutoSearchRadius =
      autoSearchRadius && autoSearchRadius > 0 ? autoSearchRadius : undefined;
    const restoredDistance =
      previousValue && previousValue > 0
        ? previousValue
        : validAutoSearchRadius || defaultRadius;

    return {
      ...filterData,
      [FilterCollectionModel.DISTANCE]: getDistanceFilterOption(
        Number(restoredDistance)
      ),
    };
  }

  return filterData;
};

export const resolveSelectedFilters = (
  enhanceDistanceFilter: boolean,
  coverageType: string,
  filterData: ProviderSearchFilters,
  selectedFilters: ProviderSearchFilters,
  enableBHVirtualCareFilterV2: boolean,
  sectionType: string,
  defaultSearchRadius = 5
): ProviderSearchFilters => {
  // Clean up array filters on Map View Page
  let updatedFilters = cleanupArrayFilters(filterData);

  // Early return if not BH coverage or enhance distance filter is disabled
  const shouldProcessFilters =
    coverageType === ReverseCoverageTypesCodes['BEHAVIORAL HEALTH'] ||
    enhanceDistanceFilter;

  if (!shouldProcessFilters) {
    return updatedFilters;
  }

  const virtualCareModel = getVirtualCareModel(
    enableBHVirtualCareFilterV2,
    coverageType,
    sectionType
  );

  const currentVirtualCareFilterValue = getFilterValue(
    updatedFilters,
    virtualCareModel
  );
  const previousVirtualCareFilterValue = getFilterValue(
    selectedFilters,
    virtualCareModel
  );
  const autoSearchRadius =
    updatedFilters?.[FilterCollectionModel.AUTO_SEARCH_RADIUS];

  // Handle virtual care distance updates
  updatedFilters = updateVirtualCareDistance(
    updatedFilters,
    selectedFilters,
    currentVirtualCareFilterValue,
    previousVirtualCareFilterValue,
    autoSearchRadius,
    defaultSearchRadius
  );

  // Handle EAP transfers
  const currentEapOfferedFilterValue = getFilterValue(
    updatedFilters,
    FilterCollectionModel.EAP_OFFERED
  );
  const previousEapOfferedFilterValue = getFilterValue(
    selectedFilters,
    FilterCollectionModel.EAP_OFFERED
  );
  const currentAoeFilterValue = getAllFilterValue(
    updatedFilters,
    FilterCollectionModel.AREA_OF_EXPERTISE
  );
  const previousAoeFilterValue = getAllFilterValue(
    selectedFilters,
    FilterCollectionModel.AREA_OF_EXPERTISE
  );
  const aoeOptions = updatedFilters[FilterCollectionModel.AREA_OF_EXPERTISE];
  const eapOptionsfromAoe = updatedFilters[
    FilterCollectionModel.AREA_OF_EXPERTISE
  ]?.filter((option) => option?.dnt_label === EAP_OPTION_LABEL);

  // Transfer EAP offered to AOE (vice-versa)
  if (currentEapOfferedFilterValue && !previousEapOfferedFilterValue) {
    updatedFilters = handleEapOfferedAdded(updatedFilters, aoeOptions);
  } else if (
    !currentEapOfferedFilterValue &&
    previousEapOfferedFilterValue &&
    currentAoeFilterValue === previousAoeFilterValue
  ) {
    updatedFilters = handleEapOfferedRemoved(updatedFilters);
  } else if (currentAoeFilterValue !== previousAoeFilterValue) {
    updatedFilters = handleAoeUpdated(
      updatedFilters,
      eapOptionsfromAoe,
      aoeOptions
    );
  }

  const eapOnlyClientOption: Option[] = JSON.parse(
    localStorage.getItem('eapOnlyClientOption') ?? '[]'
  );
  const isBccActive = isPrioritySchedulingActive(updatedFilters);
  const hasCurrentEapSelection =
    Array.isArray(updatedFilters[FilterCollectionModel.EAP_OFFERED]) &&
    updatedFilters[FilterCollectionModel.EAP_OFFERED].some(
      (option) =>
        option?.dnt_label === EAP_OPTION_LABEL ||
        option?.dnt_label === EAP_OPTION_LABEL_ES
    );

  if (isBccActive && !hasCurrentEapSelection) {
    updatedFilters[FilterCollectionModel.EAP_OFFERED] = [];
    if (
      Array.isArray(updatedFilters[FilterCollectionModel.AREA_OF_EXPERTISE])
    ) {
      updatedFilters[FilterCollectionModel.AREA_OF_EXPERTISE] = updatedFilters[
        FilterCollectionModel.AREA_OF_EXPERTISE
      ].filter((option) => option?.dnt_label !== EAP_OPTION_LABEL);
    }
  }

  if (
    eapOnlyClientOption.length > 0 &&
    (!isBccActive || hasCurrentEapSelection)
  ) {
    updatedFilters = setEAPOptionForEAPClientOnly(
      eapOnlyClientOption,
      updatedFilters
    );
  }

  return updatedFilters;
};

export const getPreviousDistanceOrDefaultOption = (
  enhanceDistanceFilter: boolean,
  coverageType: string,
  model: FilterCollectionModel,
  selectedFilters: ProviderSearchFilters,
  enableBHVirtualCareFilterV2: boolean,
  sectionType: string,
  defaultSearchRadius: number
) => {
  // If the coverage type is Behavioral Health and a previous distance value is available,
  // set the distance filter to the previous value when the virtual care filter is cleared
  const distanceFilterValue = getFilterValue(
    selectedFilters,
    FilterCollectionModel.DISTANCE
  ) as number;

  const virtualCareModel = getVirtualCareModel(
    enableBHVirtualCareFilterV2,
    coverageType,
    sectionType
  );

  if (
    (coverageType === ReverseCoverageTypesCodes['BEHAVIORAL HEALTH'] ||
      enhanceDistanceFilter) &&
    model === virtualCareModel &&
    !distanceFilterValue
  ) {
    return {
      [FilterCollectionModel.DISTANCE]: getDistanceFilterOption(
        (selectedFilters?.[FilterCollectionModel.DISTANCE]?.[0]
          ?.previousValue as number) ||
          selectedFilters?.[FilterCollectionModel.AUTO_SEARCH_RADIUS] ||
          defaultSearchRadius
      ),
    };
  }
  return {};
};

const resolvedFilters = (selectedFilters) => {
  const resolvedFilters = { ...selectedFilters };
  if (resolvedFilters?.AutoSearchRadius) {
    delete resolvedFilters.AutoSearchRadius;
  }
  Object.keys(resolvedFilters).forEach((key) => {
    if (
      resolvedFilters[key] === null ||
      resolvedFilters[key] === '' ||
      (Array.isArray(resolvedFilters[key]) &&
        resolvedFilters[key].length === 0) ||
      Object.keys(resolvedFilters[key]).length === 0
    ) {
      delete resolvedFilters[key];
    }
  });
  return resolvedFilters;
};

export const areFiltersEqual = (filtersA, filtersB) => {
  const resolvedFilterA = resolvedFilters(filtersA);
  const resolvedFilterB = resolvedFilters(filtersB);
  return JSON.stringify(resolvedFilterA) === JSON.stringify(resolvedFilterB);
};

export const getFiltersWithoutDistance = (
  searchFilters: ProviderSearchFilters
) => _.omit(searchFilters, ['Distance', 'AutoSearchRadius']);

export const processBHDefaultFacilityFilters = (section, filters) => {
  const { options } =
    filters.find(
      (filter) => filter.model === FilterCollectionModel.PROGRAM_CATEGORY
    ) || {};
  const programIDs = section?.programIDs?.replaceAll(' ', '') || '';

  const sectionName = options?.find(
    (option) => option?.value?.toString().replaceAll(' ', '') === programIDs
  );

  if (!sectionName) {
    return [];
  }

  return [
    {
      label: sectionName.label,
      value: programIDs,
      hide: true,
      dnt_label: sectionName.dnt_label,
    },
  ];
};

export const getBrowseServicesPreSelectedFilters = ({
  isBHFacility,
  virtualCare,
  section,
  formattedAOEIds,
  preSelectEapFilter,
  coverageType,
  searchRadius,
  filters,
  t,
}) => {
  const eapCodes = getAoeCodesForEAP();
  const selectedFilters: ProviderSearchFilters = {};
  if (isBHFacility) {
    const programCategory = processBHDefaultFacilityFilters(section, filters);
    if (programCategory.length) {
      selectedFilters.ProgramCategory = programCategory;
    }
  }

  if (virtualCare) {
    selectedFilters.VirtualCare = {
      label: t('FILTER_OPTIONS.VirtualCare.Y'),
      value: true,
      hide: false,
      dnt_label: translationsEnJson.FILTER_OPTIONS.VirtualCare.Y,
    };
  }
  if (formattedAOEIds.length > 0) {
    selectedFilters.AreaOfExpertise = formattedAOEIds?.map((aoeId) => ({
      label: `${aoeId}-${t('CONTENT_FILTERS.AREAS_OF_EXPERTISE')}`,
      value: aoeId,
      hide: !aoeId,
      dnt_label: translationsEnJson.CONTENT_FILTERS.AREAS_OF_EXPERTISE,
    }));
  }
  if (preSelectEapFilter) {
    selectedFilters.EapOffered = [
      {
        label: t(EAP_OPTION_LABEL),
        value: eapCodes.join(','),
        dnt_label: EAP_OPTION_LABEL,
      },
    ];
    const eapBHProgramId = getEapBHProgramId();
    if (eapBHProgramId) {
      selectedFilters.BehavioralProgramIndicator = [
        {
          label: t(EAP_OPTION_LABEL),
          value: eapBHProgramId,
          dnt_label: EAP_OPTION_LABEL,
        },
      ];
    }
  }
  const BEST_MATCH_SORT_LABEL_KEY = getBestMatchOptionKey(coverageType);
  selectedFilters[FilterCollectionModel.SORT_BY] = {
    label: t(`SORT_OPTIONS.${BEST_MATCH_SORT_LABEL_KEY}`),
    value: Constants.PROVIDER_SEARCH_SORT_BY,
    hide: true,
    dnt_label: translationsEnJson.SORT_OPTIONS[BEST_MATCH_SORT_LABEL_KEY],
  };

  selectedFilters[FilterCollectionModel.AUTO_SEARCH_RADIUS] = searchRadius;
  selectedFilters[FilterCollectionModel.DISTANCE] =
    getDistanceFilterOption(searchRadius);

  return selectedFilters;
};

export const processDefaultFacilityFilters = (section, filters) => {
  const programIDs = section?.bhFacilityProgramIds?.replaceAll(' ', '') || '';

  if (!programIDs) {
    return [];
  }

  const { options } =
    filters?.find(
      (filter) => filter?.model === FilterCollectionModel.PROGRAM_CATEGORY
    ) || {};

  const optionValuePrograms = options?.find((option) => {
    const valueArray = option?.value?.replaceAll(' ', '')?.split(',');

    return valueArray.some((programID) =>
      programIDs
        .split(',')
        .map((programID) => programID.trim())
        .includes(programID.trim())
    );
  });

  if (!optionValuePrograms) {
    return [];
  }

  return [
    {
      label: optionValuePrograms?.label,
      value: optionValuePrograms?.value,
      hide: false,
      dnt_label: optionValuePrograms?.dnt_label,
    },
  ];
};

export const getBrowseLabsAndServicesPreSelectFilters = ({
  section,
  filters,
}) => {
  const selectedFilters: ProviderSearchFilters = {};
  const programCategory = processDefaultFacilityFilters(section, filters);
  if (programCategory.length) {
    selectedFilters.ProgramCategory = programCategory;
  }
  return selectedFilters;
};

export const getIssVirtualCareSelected = (
  searchFilters: ProviderSearchFilters,
  virtualCareModel: string
) => {
  const virtualCareFilterValue = searchFilters[virtualCareModel];
  return Array.isArray(virtualCareFilterValue)
    ? virtualCareFilterValue.length > 0
    : virtualCareFilterValue?.value;
};

export const disableDistanceFilter = (
  model: FilterCollectionModel,
  isVirtualCareFilterSelected: boolean,
  selectedFilters: ProviderSearchFilters,
  enhanceDistanceFilter: boolean
) =>
  enhanceDistanceFilter &&
  model === FilterCollectionModel.DISTANCE &&
  (selectedFilters[FilterCollectionModel.VIRTUAL_CARE_OPTIONS]?.[0]?.value ===
    VirtualCareOptions.OFFERS_VIRTUAL_VISITS ||
    isVirtualCareFilterSelected);

export const renderFilterLabel = (
  showUpcomingAvailabilityFilter: boolean,
  showCoachMarkUpcomingAvailability: boolean,
  handleOpenChange: (open: boolean) => void,
  filterSortScreen: boolean,
  portalConfigCheck: boolean,
  upcomingAvailabilityMessageTitle: string,
  label: string,
  isDistance: boolean,
  distanceTitle: string,
  showSelectedOptions: boolean,
  selectedOptions: number,
  showDistanceCoachMark: boolean,
  showCoachMarkWebDistance: boolean,
  defaultSearchRadiusMessageTitle: string,
  popOverContent: React.ReactNode,
  popOverContentUpcomingAvailability: React.ReactNode,
  showPrioritySchedulingFilter?: boolean,
  showCoachMarkPriorityScheduling?: boolean,
  prioritySchedulingMessageTitle?: string,
  popOverContentPriorityScheduling?: React.ReactNode
) => {
  if (showUpcomingAvailabilityFilter) {
    return (
      <Popover
        content={popOverContentUpcomingAvailability}
        css={{
          ...compareSharePopoverStyles,
          ...coachmarkPopoverStyles,
          'abyss-popover-content-container': {
            overflow: '$popoverContentOverflow',
            maxHeight: '$popoverContentHeight',
          },
          '& .abyss-popover-content-container p': {
            marginBottom: '10px',
          },
        }}
        data-testid="popover"
        onOpenChange={handleOpenChange}
        open={showCoachMarkUpcomingAvailability && !filterSortScreen}
        position="bottom"
        style={{ zIndex: portalConfigCheck ? '49' : '999' }}
        title={upcomingAvailabilityMessageTitle}
      >
        <div
          style={{
            lineHeight: '20px',
            color: '$core.color.neutral.90',
            fontWeight: '600',
            fontSize: '16px',
          }}
        >
          {label}
        </div>
      </Popover>
    );
  }
  if (showPrioritySchedulingFilter) {
    return (
      <Popover
        content={popOverContentPriorityScheduling}
        css={{
          ...compareSharePopoverStyles,
          ...coachmarkPopoverStyles,
          'abyss-popover-content-container': {
            overflow: '$popoverContentOverflow',
            maxHeight: '$popoverContentHeight',
          },
          '& .abyss-popover-content-container p': {
            marginBottom: '10px',
          },
        }}
        data-testid="popover"
        onOpenChange={handleOpenChange}
        open={showCoachMarkPriorityScheduling && !filterSortScreen}
        position="bottom"
        style={{ zIndex: portalConfigCheck ? '49' : '999' }}
        title={prioritySchedulingMessageTitle}
      >
        <div
          style={{
            lineHeight: '20px',
            color: '$core.color.neutral.90',
            fontWeight: '600',
            fontSize: '16px',
          }}
        >
          {label}
        </div>
      </Popover>
    );
  }
  if (isDistance) {
    const distanceDiv = (
      <div
        style={{
          lineHeight: '20px',
          color: '$core.color.neutral.90',
          fontWeight: '600',
          fontSize: '16px',
        }}
      >
        {distanceTitle}
      </div>
    );
    if (showDistanceCoachMark) {
      return (
        <Popover
          content={popOverContent}
          css={{
            ...compareSharePopoverStyles,
            ...coachmarkPopoverStyles,
          }}
          data-testid="popover"
          onOpenChange={handleOpenChange}
          open={showCoachMarkWebDistance && !filterSortScreen}
          position="bottom"
          style={{ zIndex: 999 }}
          title={defaultSearchRadiusMessageTitle}
        >
          {distanceDiv}
        </Popover>
      );
    }

    return distanceDiv;
  }

  return (
    <Text fontSize="md" fontWeight="medium">
      {label} {showSelectedOptions ? `(${selectedOptions})` : ''}
    </Text>
  );
};
